import React from 'react';
import {
  FiCheckCircle,
  FiFileText,
  FiX,
  FiArrowRight,
  FiHelpCircle,
} from 'react-icons/fi';
import { useRequester, REQUEST_TYPES } from '../../context/RequesterContext';

const RequesterRequestModal = () => {
  const {
    isRequestModalOpen,
    closeRequestModal,
    form,
    handleChange,
    setRequestType,
    isSubmitting,
    submitSuccess,
    submitError,
    handleSubmit,
    organizations,
    orgsLoading,
  } = useRequester();

  const TYPE_ICONS = {
    documents: <FiFileText className="text-xl" />,
    assessment: <FiCheckCircle className="text-xl" />,
    query: <FiHelpCircle className="text-xl" />,
  };

  if (!isRequestModalOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={closeRequestModal}
      ></div>

      <div className="relative flex min-h-full items-center justify-center p-4">
        <div
          className="relative w-full max-w-xl bg-white shadow-2xl overflow-hidden flex flex-col max-h-[calc(100vh-2rem)]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Dark Header */}
          <div className="relative bg-[#53545a] text-white px-6 py-5">
            <button
              onClick={closeRequestModal}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center border border-white/20 text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Close"
            >
              <FiX className="text-lg" />
            </button>
            <div className="flex items-center gap-2 mb-3">
              <img
                src={`${process.env.PUBLIC_URL}/logo-icon1.png`}
                alt="My Risk Shield logo"
                className="w-7 h-7 object-contain"
              />
              <div className="flex flex-col leading-none">
                <span>Risk Shield</span>
                <span className="font-semibold text-[9px] tracking-widest text-[#ff6600] mt-0.5">DETECT &amp; PROTECT</span>
              </div>
            </div>
            <h2 className="text-2xl font-bold leading-tight">
              Send a <span className="italic text-[#ff6600]">request</span>
            </h2>
            <p className="text-sm text-[#919191] mt-1">Our team will respond within one business day.</p>
          </div>

          {/* Form Body */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            <div className="px-6 py-5 space-y-5">

              {/* Success banner */}
              {submitSuccess && (
                <div className="flex items-center gap-3 bg-green-50 border border-green-200 text-green-800 px-4 py-3 text-sm">
                  <FiCheckCircle className="text-green-600 text-lg flex-shrink-0" />
                  <span>Your request has been submitted! Our team will respond within one business day.</span>
                  <button type="button" onClick={closeRequestModal} className="ml-auto text-green-600 hover:text-green-800">
                    <FiX />
                  </button>
                </div>
              )}

              {/* Error banner */}
              {submitError && (
                <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700  px-4 py-3 text-sm">
                  <FiX className="text-red-500 text-lg flex-shrink-0" />
                  <span>{submitError}</span>
                </div>
              )}

              {/* Name + Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold tracking-wide text-[#53545a] mb-1.5">
                    FULL NAME <span className="text-[#ff6600]">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={form.fullName}
                    onChange={handleChange}
                    required
                    placeholder="Enter name"
                    className="w-full px-3 py-2.5  border border-[#919191]/30 bg-[#919191]/5 text-sm text-[#53545a] placeholder-[#919191] focus:outline-none focus:border-[#ff6600] focus:ring-2 focus:ring-[#ff6600]/20"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-wide text-[#53545a] mb-1.5">
                    EMAIL <span className="text-[#ff6600]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter email"
                    className="w-full px-3 py-2.5  border border-[#919191]/30 bg-[#919191]/5 text-sm text-[#53545a] placeholder-[#919191] focus:outline-none focus:border-[#ff6600] focus:ring-2 focus:ring-[#ff6600]/20"
                  />
                </div>
              </div>

              {/* Organization (target) + Your Organization (requester) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold tracking-wide text-[#53545a] mb-1.5">
                    ORGANIZATION
                  </label>
                  <select
                    name="organization"
                    value={form.organization}
                    onChange={handleChange}
                    disabled={orgsLoading}
                    className="w-full px-3 py-2.5  border border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#ff6600] focus:ring-2 focus:ring-[#ff6600]/20 disabled:opacity-60"
                  >
                    <option value="">{orgsLoading ? 'Loading…' : 'Select organization'}</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.name}>{org.name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-[#919191]">Select the organization you want to send your request to.</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-wide text-[#53545a] mb-1.5">
                    YOUR ORGANIZATION
                  </label>
                  <input
                    type="text"
                    name="requesterOrganization"
                    value={form.requesterOrganization}
                    onChange={handleChange}
                    placeholder="Enter your organization"
                    className="w-full px-3 py-2.5  border border-[#919191]/30 bg-[#919191]/5 text-sm text-[#53545a] placeholder-[#919191] focus:outline-none focus:border-[#ff6600] focus:ring-2 focus:ring-[#ff6600]/20"
                  />
                  <p className="mt-1 text-xs text-[#919191]">The organization you are requesting on behalf of.</p>
                </div>
              </div>

              {/* Request Type */}
              <div>
                <label className="block text-xs font-semibold tracking-wide text-[#53545a] mb-2">
                  REQUEST TYPE <span className="text-[#ff6600]">*</span>
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {REQUEST_TYPES.map((type) => {
                    const selected = form.requestType === type.id;
                    return (
                      <button
                        type="button"
                        key={type.id}
                        onClick={() => setRequestType(type.id)}
                        className={`flex flex-col items-center justify-center gap-2 py-4  border-2 transition-all ${
                          selected
                            ? 'border-[#ff6600] bg-[#ff6600]/10'
                            : 'border-[#919191]/30 bg-[#919191]/5 hover:border-[#919191]/50'
                        }`}
                      >
                        <div
                          className={`w-9 h-9  flex items-center justify-center ${
                            selected ? 'bg-[#ff6600] text-white' : 'bg-white text-[#919191] border border-[#919191]/30'
                          }`}
                        >
                          {TYPE_ICONS[type.id]}
                        </div>
                        <span className={`text-xs font-semibold tracking-wide ${selected ? 'text-[#ff6600]' : 'text-[#53545a]'}`}>
                          {type.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 pt-1">
                <div className="flex-1 h-px bg-[#919191]/20"></div>
                <span className="text-[11px] font-semibold tracking-wider text-[#919191]">ATTACHMENTS &amp; DETAILS</span>
                <div className="flex-1 h-px bg-[#919191]/20"></div>
              </div>

              {/* Details fields — shown for all request types */}
              <div>
                <label className="block text-xs font-semibold tracking-wide text-[#53545a] mb-1.5">
                  QUERY ABOUT <span className="text-[#ff6600]">*</span>
                </label>
                <input
                  type="text"
                  name="queryAbout"
                  value={form.queryAbout}
                  onChange={handleChange}
                  required
                  placeholder="Enter query about"
                  className="w-full px-3 py-2.5  border border-[#919191]/30 bg-[#919191]/5 text-sm text-[#53545a] placeholder-[#919191] focus:outline-none focus:border-[#ff6600] focus:ring-2 focus:ring-[#ff6600]/20"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold tracking-wide text-[#53545a] mb-1.5">
                  QUERY / MESSAGE <span className="text-[#ff6600]">*</span>
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="Enter message..."
                  className="w-full px-3 py-2.5  border border-[#919191]/30 bg-[#919191]/5 text-sm text-[#53545a] placeholder-[#919191] focus:outline-none focus:border-[#ff6600] focus:ring-2 focus:ring-[#ff6600]/20 resize-none"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-3 px-6 py-3 border-t border-[#919191]/20 bg-white">
              <div className="flex items-center gap-2 text-xs text-[#919191]">
                <span className="w-2 h-2  bg-[#919191]"></span>
                Secured &amp; encrypted
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={closeRequestModal}
                  className="px-4 py-2 text-sm font-medium text-[#53545a] border border-[#919191]/30  hover:bg-[#919191]/5"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || submitSuccess}
                  className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-[#ff6600] hover:bg-[#d96d23] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending…' : 'Send request'}
                  {!isSubmitting && <FiArrowRight />}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RequesterRequestModal;
